import { Component, computed, inject, input } from '@angular/core';
import type { SafeHtml } from '@angular/platform-browser';
import { DomSanitizer } from '@angular/platform-browser';

import type { Attachment } from '@core/attachments-types';
import type { RatingScale } from '@core/candidate-evaluation-settings-types';
import { TimeService } from '@core/common-legacy';
import { AttachmentsListComponent } from '@core/components-attachments-list-legacy';
import type { AggregatedEvaluationsAnswer } from '@core/evaluations-types';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type {
  InterviewResultAnswerOption,
  RatingLevelTranslations,
} from '@core/interview-results-types';
import { InterviewEvaluationRatingComponent } from '@core/interview-results-ui';
import { QuestionKind } from '@core/interview-templates-types';
import { provideFilteredMentions } from '@core/notes-legacy';
import { RenderDirective, WysiwygContext$ } from '@core/prosemirror-legacy/angular';
import {
  ProsemirrorDefaultConfig,
  ProsemirrorDefaultConfigDirective,
} from '@core/prosemirror-legacy/webapp';
import {
  BoxComponent,
  DividerComponent,
  HeadingComponent,
  IconComponent,
  RtTooltip,
} from '@core/user-interface-legacy';

import { EvaluationRatingScoreComponent } from '../../evaluation-rating-score/evaluation-rating-score.component';

@Component({
  selector: 'rt-candidate-offer-evaluation-reviewer-result-preview',
  templateUrl: './candidate-offer-evaluation-reviewer-result-preview.component.html',
  styleUrl: './candidate-offer-evaluation-reviewer-result-preview.component.less',
  imports: [
    AttachmentsListComponent,
    BoxComponent,
    DividerComponent,
    EvaluationRatingScoreComponent,
    HeadingComponent,
    IconComponent,
    InterviewEvaluationRatingComponent,
    ProsemirrorDefaultConfigDirective,
    RenderDirective,
    RtTooltip,
    TranslatePipe,
  ],
  providers: [
    { provide: WysiwygContext$, useFactory: () => new WysiwygContext$(null) },
    ...provideFilteredMentions(),
  ],
})
export class CandidateOfferEvaluationReviewerResultPreviewComponent {
  private readonly i18n = inject(I18nService);
  private readonly timeService = inject(TimeService);
  private readonly sanitizer = inject(DomSanitizer);

  private readonly booleanAnswers: Record<string, string> = {
    yes: this.i18n.translate('app.common.boolean.yes'),
    no: this.i18n.translate('app.common.boolean.no'),
  };

  protected readonly translations = {
    attachment: this.i18n.translate('app.print.candidate_profile.attachment'),
    skillsHeading: this.i18n.translate('app.interview.summary.skills_heading'),
  };

  protected readonly ProsemirrorConfig = ProsemirrorDefaultConfig;

  public answers = input.required<AggregatedEvaluationsAnswer[]>();

  public useProsemirror = input(false);

  public canManageCandidates = input(false);

  public ratingLevelTranslations = input<RatingLevelTranslations>();

  public score = input<number>();

  public ratingScale = input<RatingScale | null>(null);

  public ratingNote = input<string | null>(null);

  public showScoreIcon = input(false);

  protected safeRatingNote = computed<SafeHtml | null>(() => {
    const note = this.ratingNote();

    // eslint-disable-next-line ban/ban
    return note ? this.sanitizer.bypassSecurityTrustHtml(note) : null;
  });

  public isFile = (kind: QuestionKind) => QuestionKind.File === kind;

  public isSimple = (kind: QuestionKind) =>
    [
      QuestionKind.Dropdown,
      QuestionKind.SingleChoice,
      QuestionKind.String,
      QuestionKind.Text,
    ].includes(kind);

  public isMultiChoice = (kind: QuestionKind) => QuestionKind.MultipleChoice === kind;

  public isBoolean = (kind: QuestionKind) => QuestionKind.Boolean === kind;

  public isDate = (kind: QuestionKind) => QuestionKind.Date === kind;

  public isSalary = (kind: QuestionKind) => QuestionKind.Salary === kind;

  public isSection = (kind: QuestionKind) => QuestionKind.Section === kind;

  public isScorecard = (kind: QuestionKind) => QuestionKind.Scorecard === kind;

  public isSkills = (kind: QuestionKind) => QuestionKind.Skills === kind;

  public getSafeContent = (answer: AggregatedEvaluationsAnswer): SafeHtml | null =>
    // eslint-disable-next-line ban/ban
    answer.contentHtml ? this.sanitizer.bypassSecurityTrustHtml(answer.contentHtml) : null;

  public getAttachments = (answer: AggregatedEvaluationsAnswer): Attachment[] =>
    <Attachment[]>(<unknown>(answer.attachments ?? []));

  public getFormattedDate = (content: string | null | undefined) =>
    content ? this.timeService.format({ input: content, pattern: 'DD MMM YYYY' }) : '';

  public getSalary(answer: AggregatedEvaluationsAnswer): string {
    const amount = answer.content ?? '';
    const code = answer.questionOptions?.currency;
    return code ? `${amount} ${code}` : amount;
  }

  public hasAnswers(answer: AggregatedEvaluationsAnswer): boolean {
    if (!answer) return false;

    return (
      !!(answer.contentHtml || '').length ||
      !!(answer.content || '').length ||
      !!(answer.attachments || []).length ||
      !!(answer.skillRatings || []).length ||
      this.hasAnswer(answer.contents || [])
    );
  }

  public getEvaluatedSkills(answer: AggregatedEvaluationsAnswer) {
    const skills = answer.questionOptions?.skills || [];
    const ratings = answer.skillRatings || [];
    return skills.filter(skill =>
      ratings.some(rating => rating.skillId === skill.id && rating.proficiencyId !== null),
    );
  }

  public getBooleanAnswer = (answer: string | null | undefined) =>
    answer ? this.booleanAnswers[answer] : '';

  public filterEmptyOptions(answer: AggregatedEvaluationsAnswer): InterviewResultAnswerOption[] {
    return (answer.options || []).filter((option, index) => answer.contents![index]);
  }

  public filterEmptyContents(contents: string[]): string[] {
    return contents.filter(content => content && content !== '');
  }

  public trackByAnswer(index: number, answer: AggregatedEvaluationsAnswer): number {
    return answer.id;
  }

  public trackByContent(index: number, content: string): string {
    return content + index;
  }

  public trackByOption(index: number, option: InterviewResultAnswerOption): string {
    return option.title;
  }

  private hasAnswer(contents: string[]): boolean {
    return !!contents.filter(content => content && content !== '').length;
  }
}
